import { useEffect, useState } from "react";
import type { InstanceSeatsView } from "@keydials/api-client";
import { HELP_ANCHORS } from "@keydials/ui";
import { api, getAccessToken } from "../auth/AuthContext";
import { SettingsCard } from "./SettingsCard";
import {
  LICENCE_RULES,
  archivedLine,
  licensingSummary,
  poolDerivation,
  purchasedLine,
} from "./licensing";

/**
 * What this account's licence covers, and how much of it is left (KEY-497).
 *
 * Answers the question a dial administrator actually has — "can I add another person?" — before they find
 * out by being refused. Enforcement was already live when this was written: the server has refused grants
 * for want of a place since KEY-399, and every one of those refusals landed on somebody who had no way of
 * knowing how close they were.
 *
 * All three states `InstanceSeatsView` documents render, and none is collapsed into another — in
 * particular an unenforced position says "nobody is counting" rather than "0 of 0", which would be a number
 * this system cannot stand behind. The wording lives in `licensing.ts` so the People sheet's one-line
 * version of the same figure cannot drift away from this one.
 */
export function LicensingSection({ instanceId }: { instanceId: string }) {
  const [seats, setSeats] = useState<InstanceSeatsView | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading");

  useEffect(() => {
    const token = getAccessToken();
    if (!token) return;
    setState("loading");
    api
      .getInstanceSeats(token, instanceId)
      .then((s) => {
        setSeats(s);
        setState("ready");
      })
      // A 404 here means this caller may not manage the dial's members, which the menu should already have
      // stopped — but a seat position is commercial information, so failing closed rather than guessing is
      // the right way round.
      .catch(() => setState("failed"));
  }, [instanceId]);

  if (state === "loading") {
    return (
      <SettingsCard title="Licensing" subtitle="What your licence covers">
        <p style={muted}>Checking your licence…</p>
      </SettingsCard>
    );
  }

  if (state === "failed") {
    return (
      <SettingsCard title="Licensing" subtitle="What your licence covers">
        <p style={muted}>Couldn't read your licence just now. Try again in a moment.</p>
      </SettingsCard>
    );
  }

  const summary = licensingSummary(seats);
  const derivation = poolDerivation(seats);
  const purchased = purchasedLine(seats);
  const archived = archivedLine(seats);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }} data-help-anchor={HELP_ANCHORS.dialLicensingSummary}>
      <SettingsCard
        title="Licensing"
        subtitle={seats?.planName ? `Your plan: ${seats.planName}` : "What your licence covers"}
      >
        <p
          style={{
            margin: 0,
            fontSize: 15,
            fontWeight: 700,
            lineHeight: 1.5,
            color: "var(--text)",
          }}
        >
          {summary.headline}
        </p>

        {summary.state === "counted" && (
          <Meter used={seats!.used ?? 0} allowance={seats!.allowance!} full={summary.full} />
        )}

        {/* The pool is a MULTIPLIER, not a number: building a dial buys places, so the allowance moves on
            its own. An administrator who is not told that is reading a figure they cannot account for. */}
        {derivation && (
          <p style={{ ...muted, marginTop: 10 }}>
            Your licence works out as <strong style={{ color: "var(--text)" }}>{derivation}</strong>.
          </p>
        )}
        {purchased && <p style={{ ...muted, marginTop: 6 }}>{purchased}</p>}

        {archived && (
          <p style={{ ...muted, marginTop: 10 }}>
            {archived}
          </p>
        )}

        {summary.full && (
          <p style={{ ...muted, marginTop: 10 }}>
            To add somebody else, remove someone who no longer needs access, or buy more places. Guests are
            free, so a short-term visitor can still be invited from the Guests section.
          </p>
        )}
      </SettingsCard>

      {summary.state !== "unenforced" && (
        <SettingsCard
          title="What uses a place"
          subtitle="Every clause here has surprised somebody, so none of it is left to be discovered from a refusal"
        >
          <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 6 }}>
            {LICENCE_RULES.map((rule) => (
              <li key={rule} style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--text-muted)" }}>
                {rule}
              </li>
            ))}
          </ul>
        </SettingsCard>
      )}
    </div>
  );
}

/**
 * The bar. Deliberately capped at 100% and coloured on the same threshold the sentence uses, so a plan that
 * has gone OVER its allowance — which happens without anybody touching a membership, because archiving a
 * dial shrinks the pool — reads as full rather than overflowing its own frame.
 */
function Meter({ used, allowance, full }: { used: number; allowance: number; full: boolean }) {
  const pct = allowance <= 0 ? 100 : Math.min(100, Math.round((used / allowance) * 100));
  return (
    <div
      style={{ marginTop: 10 }}
      role="img"
      aria-label={`${used} of ${allowance} places used`}
    >
      <div
        style={{
          height: 8,
          borderRadius: 999,
          background: "var(--surface-inset)",
          border: "var(--border-w) solid var(--border)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 999,
            // A literal for the full state: web-user's theme has no danger token, and reaching for
            // web-admin's would be the shared-CSS trap #739 was about — those classes and variables are
            // not defined in this app.
            background: full ? "#c0392b" : "var(--accent)",
            transition: "width 160ms ease",
          }}
        />
      </div>
    </div>
  );
}

const muted: React.CSSProperties = {
  margin: 0,
  fontSize: 13.5,
  lineHeight: 1.6,
  color: "var(--text-muted)",
};
